import { DateString, fromDateString, newUuid, toDateString, Uuid, validateDateString } from "@legacy-megarax/common";
import { useProfile } from "@legacy-megarax/iam-webapp";
import { useCurrentUser } from "@legacy-megarax/react-client";
import { BreadcrumbMarkers, PageTitleManager } from "@legacy-megarax/ui-components";
import { useCustomers } from "@megarax/crm-resource-hooks";
import { CalendarViewWeek } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { matchPath, useLocation, useNavigate, useParams } from "react-router-dom-v5-compat";

import { getMegaraxPath } from "@megaron/megarax-uri";
import { useClientManager } from "@megaron/react-clients";
import { isDefined } from "@megaron/utils";

import { ActionButtons } from "./ActionButtons";
import { ActivityList } from "./ActivityList";
import { AddUnplannedVisitDialogContainer } from "./AddUnplannedVisitDialog";
import { ChangeEventDialogContainer } from "./ChangeEventDialog";
import { DaySelect } from "./DaySelect";
import { getActivityListEntries } from "./getActivityListEntries";
import { PlanRouteDialogContainer } from "./PlanRouteDialogContainer";
import { useOnVisitClose, useVisitNavigation } from "./useVisitNavigation";
import { useWorkDay } from "./useWorkDay";
import { ViewLayout } from "./ViewLayout";
import { WorkTimeCardContainer } from "./WorkTimeCard";

export const DayViewContainer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const dateString = validateDateString(params.date || "").assertOk().value;
  const userId = Number(params.userId);

  const onChangeDateString = (dateStr: DateString) =>
    navigate(
      getMegaraxPath.uri("megarax://crm/salesperson-visits/id/{id}/{view}/{date}", {
        id: String(userId),
        view: "day",
        date: dateStr,
      }),
    );
  const onGoToWeek = (dateStr: DateString) =>
    navigate(
      getMegaraxPath.uri("megarax://crm/salesperson-visits/id/{id}/{view}/{date}", {
        id: String(userId),
        view: "week",
        date: dateStr,
      }),
    );

  const date = fromDateString(dateString);

  const profile = useProfile({ id: userId });

  const regionQuery = useClientManager("crm")
    .regionsQuery()
    .useQuery({ ownerEmail: profile?.user?.email }, { enabled: !!profile?.user?.email });

  const { workDay, isLoading: workDayIsLoading, refetch: refetchWorkDay } = useWorkDay(date, userId);
  const visitScheduleQuery = useClientManager("legacyMegarax")
    .visitScheduleQuery()
    .useQuery({ date: toDateString(date), userId });
  const interactionsQuery = useClientManager("crm")
    .ownerInteractionQuery()
    .useQuery({ ownerEmail: profile?.user.email ?? "", dates: [date] }, { enabled: profile !== undefined });

  const interactionCustomerUuids = interactionsQuery.data?.items?.map((i) => i.customerId) ?? [];
  const visitCustomerUuids = visitScheduleQuery.data?.visits?.map((v) => v.customerUuid ?? null).filter(Boolean) ?? [];

  const customerUuids = [...interactionCustomerUuids, ...visitCustomerUuids];
  const customerMap = useCustomers(customerUuids);

  const { closeVisit, openVisit } = useVisitNavigation();

  const openNewBreakDialog = () => navigate(`${pathname}/break/${newUuid()}`);

  const entries =
    isDefined(visitScheduleQuery.data) && isDefined(interactionsQuery.data) && isDefined(workDay)
      ? getActivityListEntries(visitScheduleQuery.data, interactionsQuery.data.items, workDay, customerMap)
      : undefined;

  const currentUser = useCurrentUser();
  const isMyDay = userId === currentUser?.id;

  useOnVisitClose(() => {
    refetchWorkDay();
    interactionsQuery.refetch();
  });

  const openNewVisitDialog = () => navigate(`${pathname}/nowa_wizyta`);
  const openChangeEventDialog = (eventUuid: Uuid) => navigate(`${pathname}/event/${eventUuid}`);
  const openNewEventDialog = () => openChangeEventDialog(newUuid());
  const openPlanRouteDialog = () => navigate(`${pathname}/zaplanuj`);

  const basePath = getMegaraxPath.uri("megarax://crm/salesperson-visits/id/{id}/{view}/{date}", {
    id: String(userId),
    view: "day",
    date: dateString,
  });

  const isNewVisitDialogOpen = pathname.endsWith("nowa_wizyta");
  const changeEventMatchPath = matchPath(`${basePath}/event/:eventUuid`, pathname);
  const isPlanRouteDialogOpen = pathname.endsWith("zaplanuj");

  return (
    <>
      <PageTitleManager title={dateString} />
      <BreadcrumbMarkers
        breadcrumbs={[
          { title: "Handlowcy", to: getMegaraxPath.uri("megarax://crm/regions"), id: "salespeople" },
          {
            title: profile?.displayedName ?? "?",
            to: regionQuery.data?.items[0]?.name
              ? getMegaraxPath.uri("megarax://crm/regions/id/{id}", {
                  id: encodeURIComponent(regionQuery.data?.items[0]?.name),
                })
              : getMegaraxPath.uri("megarax://crm/regions"),
            id: "salesperson",
          },
          { title: dateString, id: "date", to: pathname },
        ]}
      />
      {isNewVisitDialogOpen && <AddUnplannedVisitDialogContainer date={date} onClose={() => navigate(basePath)} />}
      {changeEventMatchPath && workDay && (
        <ChangeEventDialogContainer
          date={date}
          eventUuid={(changeEventMatchPath.params.eventUuid as Uuid) || ("" as Uuid)}
          events={workDay.events}
          isOpen={true}
          onChanged={refetchWorkDay}
          onClose={() => navigate(basePath)}
        />
      )}
      {isPlanRouteDialogOpen && (
        <PlanRouteDialogContainer
          date={date}
          onChange={() => {
            setTimeout(() => {
              refetchWorkDay();
              visitScheduleQuery.refetch();
            }, 1700);
          }}
          onClose={() => navigate(basePath)}
          userId={userId}
        />
      )}
      <ViewLayout
        dateSelect={<DaySelect date={date} onChange={(date) => onChangeDateString(toDateString(date))} />}
        activityList={
          <ActivityList
            entries={entries}
            onEventEdit={(eventUuid) => openChangeEventDialog(eventUuid)}
            onVisitSeeMore={openVisit}
          />
        }
        alerts={null}
        buttons={
          <ActionButtons
            isMyDay={isMyDay}
            onBreakClick={openNewBreakDialog}
            onEventClick={openNewEventDialog}
            onVisitClick={openNewVisitDialog}
            onRouteClick={openPlanRouteDialog}
          />
        }
        workTime={<WorkTimeCardContainer userId={userId} date={date} />}
        isLoading={workDayIsLoading || visitScheduleQuery.isLoading || interactionsQuery.isLoading}
        closeVisit={closeVisit}
        viewSwitchButton={
          <IconButton onClick={() => onGoToWeek(dateString)}>
            <CalendarViewWeek />
          </IconButton>
        }
      />
    </>
  );
};
